// Story 7-FU-D (ADO-1217), AC5 — the shared one-click add-to-basket confirmation.
//
// Three surfaces render an identical per-tile Add button: the ProductCard inline
// add (Story 7.9), the RecommendationRail tile (Story 7.12) and the
// SubstitutesPanel row (Story 7.11). Each awaits `addToBasket(sku)` then flashes a
// transient "Added ✓" for a short window (or clears it on a failed/blocked add).
// Extracting the logic here keeps the three from drifting AND is the single place
// the unmount guard lives.
//
// Story 7.12 Iteration 2 (ADO-911), AC7 — this hook is ALSO the single place the
// Linked Product upsell is triggered from. Because every add-to-basket
// affordance in the app already funnels through `confirm()`, wiring
// `useUpsellModalStore.openUpsell(sku, name, variant)` here satisfies "the upsell
// fires on ALL add-to-basket entry points" (Marika, 2026-07-29) with one call
// site instead of five, and makes a NEW add button upsell-capable by default —
// `productName` is REQUIRED (PR #168 review) so "by default" is enforced by the
// compiler and a new Add button cannot omit the trigger silently.
//
// ADO-911 design refresh (2026-08-14): the upsell now has TWO presentations, and
// which one an add opens is a property of WHERE the add came from — a PDP add
// opens the full MODAL, a search/listing add opens the non-invasive TOAST. So
// `upsellVariant` is REQUIRED too, on the same reasoning as `productName`: the
// origin is known only at the call site, and a default would silently give a new
// Add button the wrong presentation. Every caller declares its variant.
//
// Story 7.9 (ADO-1141), AC1: this hook is also the single place the app-wide "Added to
// cart" toast (Figma 4002:14600) is raised, beside the inline "Added ✓". It fires on a
// successful add only, and reports the quantity THAT add carried (`Qty {n} · {name}`),
// never the line's new total, as the `addedToCart` kind: a fixed-width pill where a long
// product name wraps to two lines and then ends in an ellipsis. Stepper and typed quantity
// edits do not come through here, so they raise no toast of their own; but once the basket
// has loaded, the toast names its cart line, and while it is on screen an edit of that line
// moves its quantity with it (`ToastHost`). `useToastStore` shows one toast at a time, so a rapid run of adds leaves
// only the latest on screen.
//
// The gap this closes: every call site already clears its confirm timer on
// unmount, but the `setConfirmed` that runs the INSTANT the await resolves fired
// even if the tile unmounted mid-add (a collapsed section, a live-availability
// push removing the tile, the panel closing). That is a state update + a timer
// start on a dead component. A `mountedRef` bails after the await.

import { useEffect, useRef, useState } from 'react';
import { useBasketStore } from './useBasketStore';
import { useUpsellModalStore, type UpsellVariant } from '../upsell/useUpsellModalStore';
import { addedToCartDescription, useToastStore } from '../toast/useToastStore';
import { formatSentTime } from '../shared/deliveryDayDisplay';

/** Story 8.3 (ADO-2012), Task 13.5 (card 11): the toast an add shows when it went into a new
 *  cart because the cart on screen had been sent (the designer's copy, the sent time as on card
 *  10: the time alone when it is today, in Dublin time, D-27). */
export const NEW_CART_STARTED_TITLE = 'Started a new cart';
export function newCartStartedDescription(sentAt: string, nowMs: number): string {
  return `Your last order was sent at ${formatSentTime(sentAt, nowMs)}.`;
}

/** How long the transient "Added ✓" confirmation shows before clearing (ms). */
const CONFIRM_WINDOW_MS = 2000;

export interface AddConfirmation {
  /** True during the transient post-add confirmation window. */
  readonly confirmed: boolean;
  /** Add `quantity` (default 1) of the SKU to the basket, then run the guarded
   *  confirm/clear semantics (Story 8.2 ADO-2011: one add carries the quantity).
   *  Resolves `true` when the add landed, `false` when it failed or was blocked. */
  readonly confirm: (quantity?: number) => Promise<boolean>;
  /** Story 8.3 (ADO-2012), Task 13.5 (card 11): the last add was made from a view of the cart
   *  before its latest send, so it went into the emptied cart (Enabler E8-G, ADO-3109, AC8), the
   *  "new cart" of the copy (for the scanner's own announcement). */
  readonly startedNewCart: boolean;
}

/** Story 7.12 Iteration 2 (ADO-911), AC7 — Linked Product upsell wiring. */
export interface AddConfirmationOptions {
  /** The product's display name, for the "{product} added to order" badge.
   *
   *  REQUIRED, and the options bag with it (PR #168 review). It was optional
   *  through Iteration 2's migration so an un-migrated call site would stay
   *  INERT rather than break — but that is exactly how quick-add shipped with no
   *  modal: `useAddConfirmation(sku)` compiled, ran, and silently skipped
   *  `openUpsell`, failing nothing in tests or at runtime. AC9 ("no linked ⇒ no
   *  modal") is protected inside the store, but the TRIGGER was not protected
   *  anywhere. All four call sites pass a name now, so the migration reason is
   *  spent, and requiring it makes the next Add button a COMPILE error instead
   *  of a silent AC7 regression. */
  readonly productName: string;
  /** ADO-911 design refresh — which presentation this button's add opens: the PDP
   *  `'modal'` or the search/listing `'toast'`. REQUIRED (see the header note):
   *  the origin is known only here, and a default would misroute a new button. */
  readonly upsellVariant: UpsellVariant;
  /** Set `false` to suppress the upsell for this button. Used by the upsell's OWN
   *  Add buttons so adding a linked product can't stack a second upsell on the
   *  first. Defaults to `true`. */
  readonly upsell?: boolean;
  /** Story 7.9 (ADO-1141), AC1: set `false` to suppress the app-wide "Added to cart"
   *  toast for this button. Used by the camera scanner, an aria-modal dialog that draws
   *  and announces its own copy of the toast inside the dialog. Defaults to `true`. */
  readonly toast?: boolean;
}

export function useAddConfirmation(
  sku: string,
  { productName, upsellVariant, upsell = true, toast = true }: AddConfirmationOptions,
): AddConfirmation {
  const addToBasket = useBasketStore((s) => s.add);
  const [confirmed, setConfirmed] = useState(false);
  const [startedNewCart, setStartedNewCart] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The mounted guard (AC5): the cleanup below clears the PENDING timer, but the
  // post-await state update needs its own guard so an add that resolves after the
  // tile unmounted performs no state update and starts no timer on a dead tile.
  const mountedRef = useRef(true);
  useEffect(() => {
    // Set true in the SETUP body (not just via the useRef initializer) so the
    // guard survives React <StrictMode>'s dev-only setup→cleanup→setup remount:
    // the cleanup flips it false, and this restores it on the second setup —
    // otherwise the ref would stay false while the tile is actually mounted and
    // every post-await setConfirmed would silently bail.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (confirmTimer.current !== null) clearTimeout(confirmTimer.current);
    };
  }, []);

  const confirm = async (quantity = 1): Promise<boolean> => {
    // What the line held before this add, read before it so the toast's live quantity
    // (`ToastHost`) is what the add actually put there, even when the server adjusts it.
    // Only a loaded basket knows that: before the read lands, or after it failed, the
    // add's reconcile brings in the server's whole line over a 0 baseline, so the toast
    // keeps the fixed quantity it was raised with instead.
    const { hydrated, error, quantityBySku } = useBasketStore.getState();
    const quantityBeforeAdd = hydrated && !error ? (quantityBySku.get(sku) ?? 0) : null;
    await addToBasket(sku, quantity);
    // Read THIS sku's own entry — never "whoever wrote the slot last". A
    // concurrent failed add on another SKU must not make this add flash "Added ✓"
    // (and fire the upsell) on a line that actually landed, nor vice versa (AC1).
    // Story 8.3 (ADO-2012): an add refused because the line changed since it was seen is a
    // conflict, not an error, and added nothing either. The add drops the product's earlier
    // conflict as it starts, so one present now is this add's.
    const settled = useBasketStore.getState();
    const failed = settled.mutationErrors.has(sku) || settled.lineConflicts.has(sku);

    // Story 7.12 Iteration 2 (AC7) — fire the Linked Product upsell on a
    // SUCCESSFUL add, from EVERY entry point (PDP hero, quick-add, listing card,
    // substitutes row, carousel tile). Deliberately BEFORE the mounted guard and
    // deliberately NOT awaited:
    //   • the modal is global store state, so it must still open if the tile that
    //     triggered the add unmounted mid-flight (a collapsed panel, a live
    //     availability push);
    //   • awaiting the linked-products fetch would stall the "Added ✓" flash
    //     behind a network round-trip.
    // AC9 ("no Linked Products ⇒ no upsell") is enforced inside `openUpsell`.
    if (!failed && upsell) {
      void useUpsellModalStore.getState().openUpsell(sku, productName, upsellVariant);
    }
    // Story 8.3 (ADO-2012), Task 13.5 (card 11, D-25): the add went into the emptied cart (AC8)
    // because the cart on screen had been sent: "Started a new cart" replaces the add-to-cart toast, with the
    // same toast behaviour, and shows even where that toast is switched off (the scanner).
    const newCartSentAt = failed ? null : useBasketStore.getState().takeNewCartStarted(sku);
    if (newCartSentAt !== null) {
      useToastStore.getState().showToast({
        title: NEW_CART_STARTED_TITLE,
        description: newCartStartedDescription(newCartSentAt, Date.now()),
        kind: 'addedToCart',
      });
    }
    // Story 7.9 (AC1): the "Added to cart" toast, also before the mounted guard, since it
    // too is global store state that must show when the tile unmounted mid-add.
    if (!failed && toast && newCartSentAt === null) {
      useToastStore.getState().showToast({
        title: 'Added to cart',
        description: addedToCartDescription(quantity, productName),
        kind: 'addedToCart',
        ...(quantityBeforeAdd === null
          ? {}
          : { cartLine: { skuCode: sku, productName, quantityBeforeAdd } }),
      });
    }

    // Unmounted during the in-flight add — do nothing (no setConfirmed, no timer).
    if (!mountedRef.current) return !failed;
    setStartedNewCart(newCartSentAt !== null);
    // Confirm only when the add actually landed — a failed/blocked add records an
    // error for THIS sku (surfaced by the caller), and must not flash "Added".
    if (!failed) {
      setConfirmed(true);
      if (confirmTimer.current !== null) clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => setConfirmed(false), CONFIRM_WINDOW_MS);
    } else {
      // A failed/blocked add must not sit beside a stale "Added ✓" from a prior
      // success still inside its window — clear it at once so the button and the
      // error alert can never contradict each other.
      if (confirmTimer.current !== null) clearTimeout(confirmTimer.current);
      setConfirmed(false);
    }
    return !failed;
  };

  return { confirmed, confirm, startedNewCart };
}
